import moment from "moment";
import { ElMessage, ElMessageBox, ElNotification } from "element-plus";
import { Customer } from "~/types/customer";
import { FraudChecker } from "~/types/fraudChecker";
import { Comment } from "~/types/comment";
import { Referral } from "~/types/referral";
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
  downloadWithSavePicker,
} from "~/apis/requests/useApiCall";
import { TableDataWithPagination } from "~/types/generics/tableDataWithPagination";
import { Pagination } from "~/types/pagination";
import { exportToExcel } from "~/utils/dataTableHelper";
import { Profile, MergePreview } from "~/types/profile";
import { AssociatedProduct } from "~/types/associatedProduct";
import { useTopBarBadgeStore } from "~/stores/topBarBadgeStore";
import { todayLocal } from "~/utils/dateHelper";

const isFilterValueSet = (value: unknown) =>
  value !== null && value !== undefined && value !== "";

export const useCustomerProfileStore = definePiniaStore(
  "customerProfileStore",
  {
    state: () => ({
      _tableData: [] as Profile[],
      _loading: true,
      _fraudCheckerTableLoading: false,
      _sourceWiseProfileCountGraphLoading: false,
      _sourceWiseProfileCountGraphData: [],
      // `days` is driven by the card's Expand toggle: 7 collapsed, 30 expanded.
      sourceWiseProfileCountFilter: {
        days: 7,
        channel_id: null as number | null,
        channel_type: "",
        selected_channel: "",
      },
      _referralData: [] as Referral[],

      _filterData: {} as Partial<Profile>,
      _pagination: {} as Pagination,
      showAdvancedFiltersModal: false,
      _profile: {} as Profile,
      _selectedProfileForEdit: {} as Profile,
      _selectedProfileIds: [] as number[],
      _simpleProfiles: [] as Profile[],
      _profileImportColumns: [] as Profile[],
      _simpleFilter: {},
      _mergePreview: null as MergePreview | null,
      _mergePreviewLoading: false,
      _mergeConflictPhone: "" as string,
      _fraudCheckerData: null as FraudChecker | null,
      _fraudCheckerPhone: "" as string,
      _mergeRedirectMode: "profile" as "profile" | "stay",
      showProfileEditModal: false,
      showProfileFiltersModal: false,
      showProfileCreateModal: false,
      showBulkUpload: false,
      showProfileCreateDrawer: false,
      showProfileMergeModal: false,
      showFraudCheckerModal: false,
      showAssignProfileModal: false,

      _profileComments: [] as Comment[],
      _profileOtherAttachments: [],
    }),

    actions: {
      async getFraudCheckerTableData(phone: string) {
        this._fraudCheckerTableLoading = true;

        const url = `cod-fraud-check?phone=${phone}`;
        this.$state._fraudCheckerPhone = phone;
        const { response, error } = await getRequest<FraudChecker>(url);

        if (!error) {
          this.$state._fraudCheckerData = response.data; // add _fraudCheckerData in your state
        }

        this._fraudCheckerTableLoading = false;
      },

      async getDashboardKpisData(): Promise<void> {
        this._sourceWiseProfileCountGraphLoading = true;

        const filter = this.$state.sourceWiseProfileCountFilter;
        const params = new URLSearchParams({
          from_date: moment()
            .subtract(filter.days - 1, "days")
            .format("YYYY-MM-DD"),
          to_date: moment().format("YYYY-MM-DD"),
        });

        if (filter.channel_id) {
          params.append("channel_id", String(filter.channel_id));
          params.append("channel_type", filter.channel_type);
        }

        const url = `kpis/source-wise-profile-count?${params.toString()}`;

        const { response, error } = await getRequest<Object>(url);

        if (!error) {
          this.$state._sourceWiseProfileCountGraphData = response.data;
        }

        this._sourceWiseProfileCountGraphLoading = false;
      },

      async getSimpleReferrals(
        channelId: number,
        channelType: "meta_page" | "whatsapp_account",
      ) {
        const url = `desk/referrals/simple?channel_id=${channelId}&channel_type=${channelType}`;

        const { response, error } = await getRequest<Referral[]>(url);

        if (!error && response) {
          this.$state._referralData = response.data;
        } else {
          this.$state._referralData = [];
          console.error("Failed to fetch referrals", error);
        }
      },

      async getProfiles(pageNo = 1, sortBy = "id", ascending = true) {
        this._loading = true;
        const sortDir = ascending ? "asc" : "desc";
        let url = `profiles?page=${pageNo}&pagination=true&sort_column=${sortBy}&dir=${sortDir}`;
        for (const [key, value] of Object.entries(this._filterData)) {
          if (!isFilterValueSet(value)) continue;
          url += `&${key}=${encodeURIComponent(value)}`;
        }
        const { response, error } = await getRequest<
          TableDataWithPagination<Profile>
        >(url);
        if (!error) {
          const { data, ...pagination } = response.data;
          this.$state._tableData = data;
          this.$state._pagination = pagination;
          this._loading = false;
        }
      },
      async exportTable() {
        let url = `profiles/export?`;
        for (const [key, value] of Object.entries(this._filterData)) {
          if (!isFilterValueSet(value)) continue;
          url += `${key}=${encodeURIComponent(value)}&`;
        }
        const date = todayLocal();
        const fileName = `profiles-report-${date}`;
        await downloadWithSavePicker(url, fileName);
      },
      async getProfileByProfileableId(data: {
        profileable_id: number;
        profileable_type: string;
      }) {
        let url = `profiles/byProfileable?`;
        for (const [key, value] of Object.entries(data)) {
          url += `&${key}=${encodeURIComponent(value)}`;
        }
        const { response, error } = await getRequest<Object>(url);
        if (!error) {
          this.$state._profile = response.data;
        }
      },
      async getProfileComments(data: {
        commentable_id: number;
        commentable_type: string;
      }) {
        const params = new URLSearchParams({
          commentable_id: data.commentable_id.toString(),
          commentable_type: data.commentable_type,
        });

        const url = `comments?${params.toString()}`;

        const { response, error } = await getRequest<Comment[]>(url);

        if (!error) {
          this.$state._profileComments = response.data;
        }
      },
      async getProfileOtherAttachments(profileId: number) {
        const url = `profiles/${profileId}/other-attachments`;

        const { response, error } = await getRequest<any[]>(url);

        if (!error) {
          this.$state._profileOtherAttachments = response.data;
        }
      },

      async createProfile(
        profile: FormData,
        attachments: { file: File; name: string; description?: string }[] = [],
      ) {
        const payload = profile;

        attachments.forEach((attachment, index) => {
          if (!attachment.file) return;

          payload.append(`attachments[${index}][file]`, attachment.file);
          payload.append(
            `attachments[${index}][name]`,
            attachment.name || attachment.file.name,
          );
          payload.append(
            `attachments[${index}][description]`,
            attachment.description ?? "",
          );
        });

        const { response, error } = await postRequest<FormData>(
          `profiles`,
          payload,
          true,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          },
        );

        if (!error) {
          return response.data;
        }

        const errorMessages = Object.values(error.response.data.errors)
          .flat()
          .map((msg) => `<p class="text-red-500">${msg}</p>`)
          .join("");

        ElMessageBox.alert(errorMessages, "Error", {
          dangerouslyUseHTMLString: true,
          confirmButtonText: "OK",
          type: "error",
        });

        return null;
      },

      async getProfile(profileId: number) {
        const { response, error } = await getRequest<Profile>(
          `profiles/${profileId}`,
        );
        if (!error) {
          this.$state._profile = response.data;
          return response.data;
        }
      },
      async getProfileByPhone(phone: string) {
        const { response, error } = await getRequest<Profile>(
          `profiles/phones/${phone}`,
        );
        if (!error) {
          return response.data;
        }
      },
      async updateProfile(profile) {
        const { response, error } = await putRequest<Profile>(
          `profiles/${profile.id}`,
          profile,
        );
        if (!error) {
          return response.data;
        }

        const errors = error.response?.data?.errors;

        if (errors) {
          const errorMessages = Object.values(errors)
            .flat()
            .map((msg) => `<p class="text-red-500">${msg}</p>`)
            .join("");

          ElMessageBox.alert(errorMessages, "Error", {
            dangerouslyUseHTMLString: true,
            confirmButtonText: "OK",
            type: "error",
          });
        }

        return null;
      },
      async updateDefaultPhone(
        profileId: number,
        phoneId: number,
        newPhone: string,
      ) {
        const { response, error } = await putRequest(
          `profiles/${profileId}/phones/${phoneId}`,
          {
            phone: newPhone,
            is_default: true,
          },
        );
        if (!error) {
          return response.data;
        }
        return null;
      },
      async createPhone(profileId: number, phone: string) {
        const { response, error } = await postRequest(
          `profiles/${profileId}/phones`,
          {
            phone,
            is_default: true,
          },
        );
        if (!error) {
          return response.data;
        }
        return null;
      },
      async createAddress(profileId: number, payload: any) {
        const { response, error } = await postRequest(
          `profiles/${profileId}/addresses`,
          payload,
        );
        if (!error) {
          return response.data;
        }
      },

      async updateAddress(profileId: number, addressId: number, payload: any) {
        const { response, error } = await putRequest(
          `profiles/${profileId}/addresses/${addressId}`,
          payload,
        );
        if (!error) {
          return response.data;
        }
      },

      async updateProfileRelations(profile) {
        const { response, error } = await putRequest<Profile>(
          `profiles/${profile.id}/relations`,
          profile,
        );

        if (!error) {
          return response.data;
        } else {
          const flatErrors = Object.values(error.response?.data?.errors || {})
            .flat()
            .map((msg) => String(msg));

          const duplicatePhoneMessage = flatErrors.find((msg) =>
            msg.includes("already linked to another profile"),
          );

          if (duplicatePhoneMessage) {
            const attemptedPhone =
              profile?.phones?.[profile.phones.length - 1]?.phone || "";

            this.$state._mergeConflictPhone = attemptedPhone;
            try {
              await ElMessageBox.confirm(
                `<p class="text-red-500">${duplicatePhoneMessage}</p>`,
                "Warning",
                {
                  dangerouslyUseHTMLString: true,
                  confirmButtonText: "Merge",
                  cancelButtonText: "OK",
                  distinguishCancelAndClose: true,
                  type: "warning",
                },
              );

              await this.setSelectedProfileForEdit(profile.id);
              this.toggleProfileMergeModal(true);
            } catch {
              this.$state._mergeConflictPhone = "";
              this.$state._mergeRedirectMode = "profile";
            }

            return null;
          }

          const errorMessages = flatErrors
            .map((msg) => `<p class="text-red-500">${msg}</p>`)
            .join("");

          ElMessageBox.alert(errorMessages, "Warning", {
            dangerouslyUseHTMLString: true,
            confirmButtonText: "OK",
            type: "warning",
          });
        }

        return null;
      },

      setMergeRedirectMode(mode: "profile" | "stay") {
        this.$state._mergeRedirectMode = mode;
      },

      // Fetches one simple-profile list and RETURNS it, leaving the shared
      // `_simpleProfiles` alone. A dozen selects across leads, opportunities,
      // orders and profiles all read that one array, so a caller needing its
      // own narrower list has to keep it locally rather than narrow the shared
      // one. Returns null when the request failed, which callers read as "keep
      // whatever you already had" rather than "there are none".
      async fetchSimpleProfiles(
        filter: Record<string, any> = {},
      ): Promise<Profile[] | null> {
        let url = `profiles/simple?`;
        for (const key in filter) {
          // @ts-ignore
          url += `${key}=${encodeURIComponent(filter[key])}&`;
        }
        const { response, error } = await getRequest<Profile[]>(url);

        return error ? null : response.data;
      },
      async getSimpleProfiles() {
        const profiles = await this.fetchSimpleProfiles(this._simpleFilter);

        if (profiles) {
          this.$state._simpleProfiles = profiles;
        }
      },
      async addAssociatedProduct(
        profileId: number,
        product: AssociatedProduct,
      ) {
        const { response, error } = await postRequest<AssociatedProduct>(
          `profiles/${profileId}/products`,
          product,
        );
        if (!error) {
          return null;
        }
        return error.response?.data;
      },
      async updateAssociatedProduct(
        profileId: number,
        product: AssociatedProduct,
      ) {
        const { response, error } = await putRequest<AssociatedProduct>(
          `profiles/${profileId}/products/${product.id}`,
          product,
        );
        if (!error) {
          return null;
        }
        return error.response?.data;
      },
      async deleteAssociatedProduct(profileId: number, associatedId: number) {
        return await deleteRequest(
          `profiles/${profileId}/products/${associatedId}`,
        );
      },

      async sendBulkSms(data: any) {
        let url = "profiles/send-bulk-sms?";

        if (!data.profile_ids || data.profile_ids.length === 0) {
          data.profile_ids = null;
          for (const [key, value] of Object.entries(this._filterData)) {
            if (!isFilterValueSet(value)) continue;
            url += `&${key}=${encodeURIComponent(value)}`;
          }
        }

        const { response, error } = await postRequest<any>(url, data);
        if (!error) return response.data;
        return null;
      },

      async getMergePreview(parentId: number, childId?: number | null) {
        this._mergePreviewLoading = true;
        const url = childId
          ? `profiles/merge-preview?parent_id=${parentId}&child_id=${childId}`
          : `profiles/merge-preview?parent_id=${parentId}`;
        const { response, error } = await getRequest<MergePreview>(url);
        this._mergePreviewLoading = false;

        if (error) {
          this._mergePreview = null;
          return { error };
        }

        this._mergePreview = response.data;
        return { response };
      },

      clearMergePreview() {
        this._mergePreview = null;
      },

      async mergeProfiles(profile_ids: number[], auto_merge: boolean) {
        const { response, error } = await postRequest(`profiles/merge`, {
          profile_ids,
          auto_merge,
        });
        return { response, error };
      },

      async getProfilesImportColumns() {
        console.log("hellohello");
        const url = `profile-import/settings`;

        const { response, error } = await getRequest<Profile[]>(url);
        if (!error) {
          this.$state._profileImportColumns = response.data;
          return response.data;
        }
        if (error) {
          ElMessageBox({
            title: "Error",
            message:
              error?.response?.data?.message ||
              "An error occurred while getting profile import columns",
            type: "error",
          });
        }
        return null;
      },

      async updateProfileImportColumns(data: any) {
        const { response, error } = await putRequest<any>(
          "profile-import/settings",
          data,
          true,
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        return { response, error };
      },
      async profilesBulkInsert(data: any) {
        const { response, error } = await postRequest<any>(
          "profile-import",
          data,
          true,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          },
        );

        return { response, error };
      },

      async previewBulkLeadFromProfiles(payload: {
        product_id: number;
        duplicate_handling: string;
        filters?: Record<string, any>;
      }) {
        const { response, error } = await postRequest<any>(
          "crm/leads/bulk-from-profiles/preview",
          payload,
        );

        if (!error) {
          return response.data;
        }

        const errors = error.response?.data?.errors;
        const errorMessages = errors
          ? Object.values(errors)
              .flat()
              .map((msg) => `<p class="text-red-500">${msg}</p>`)
              .join("")
          : `<p class="text-red-500">${
              error.response?.data?.message || "Failed to fetch profile count"
            }</p>`;

        ElMessageBox.alert(errorMessages, "Error", {
          dangerouslyUseHTMLString: true,
          confirmButtonText: "OK",
          type: "error",
        });

        return null;
      },

      async createBulkLeadFromProfiles(
        leadPayload: Record<string, any>,
        bulkContext: {
          product_id: number;
          duplicate_handling: string;
          filters?: Record<string, any>;
        },
      ) {
        // The BE controller pulls `product_id`, `duplicate_handling` and
        // `filters` out of the validated payload and treats the rest as the
        // per-lead data, so all three travel in the same request body.
        const payload = {
          ...leadPayload,
          product_id: bulkContext.product_id,
          duplicate_handling: bulkContext.duplicate_handling,
          filters: bulkContext.filters ?? {},
        };

        const { response, error } = await postRequest<any>(
          "crm/leads/bulk-from-profiles",
          payload,
        );

        if (!error) {
          // Each new lead can carry follow-ups, and the form defaults them to
          // the acting agent — so the top-bar follow-ups badge has to be
          // recounted, the same way the single-lead create path does.
          if (leadPayload.follow_ups?.length) {
            await useTopBarBadgeStore().getTopBarBadgeCount();
          }
          return response.data;
        }

        const errors = error.response?.data?.errors;
        const errorMessages = errors
          ? Object.values(errors)
              .flat()
              .map((msg) => `<p class="text-red-500">${msg}</p>`)
              .join("")
          : `<p class="text-red-500">${
              error.response?.data?.message || "Failed to create bulk leads"
            }</p>`;

        ElMessageBox.alert(errorMessages, "Error", {
          dangerouslyUseHTMLString: true,
          confirmButtonText: "OK",
          type: "error",
        });

        return null;
      },

      async setSelectedProfileForEdit(profileId: number) {
        const { response, error } = await getRequest<Profile>(
          `profiles/${profileId}`,
        );
        if (!error) {
          this.$state._selectedProfileForEdit = response.data;
        }
      },
      setSelectedProfiles(ids: number[]) {
        this._selectedProfileIds = ids;
      },
      /**
       * The active profile filters, for a bulk action that has to reach the same
       * rows the list is showing. Mirrors getProfiles() query building: unset
       * values are dropped so an untouched filter is not sent as empty.
       */
      profileFilterSnapshot(): Record<string, any> {
        const filters: Record<string, any> = {};

        for (const [key, value] of Object.entries(this._filterData)) {
          if (!isFilterValueSet(value)) continue;
          filters[key] = value;
        }

        return filters;
      },
      setBulkUploadModalVisibility(value: boolean) {
        this.showBulkUpload = value;
      },
      toggleProfileFiltersModal() {
        this.showProfileFiltersModal = !this.showProfileFiltersModal;
      },
      toggleProfileCreateModal() {
        this.showProfileCreateModal = !this.showProfileCreateModal;
      },
      toggleProfileEditModal() {
        this.showProfileEditModal = !this.showProfileEditModal;
      },
      toggleProfileCreateDrawer() {
        this.showProfileCreateDrawer = !this.showProfileCreateDrawer;
      },
      toggleProfileMergeModal(val: boolean) {
        this.showProfileMergeModal = val;
      },
      setFilterData(filterData: Partial<Profile>) {
        this._filterData = filterData;
      },
      setSimpleFilter(filter: any) {
        this._simpleFilter = filter;
      },
      setShowAdvancedFiltersModalVisibility(value: boolean) {
        this.showAdvancedFiltersModal = value;
      },
      setShowAssignProfileModalVisibility(value: boolean) {
        this.showAssignProfileModal = value;
      },
    },
    getters: {
      tableData: (state) => state._tableData,
      pagination: (state) => state._pagination,
      profile: (state) => state._profile,
      selectedProfileForEdit: (state) => state._selectedProfileForEdit,
      simpleProfiles: (state) => state._simpleProfiles,
      mergePreview: (state) => state._mergePreview,
      mergePreviewLoading: (state) => state._mergePreviewLoading,
      fraudCheckerTableLoading: (state) => state._fraudCheckerTableLoading,
      fraudCheckerPhone: (state) => state._fraudCheckerPhone,
      comments: (state) => state._profileComments,
      otherAttachments: (state) => state._profileOtherAttachments,
      referralData: (state) => state._referralData,
      profileImportColumns: (state) => state._profileImportColumns,
    },
  },
);
